/**
 * DEX Relay — hybrid capture layer for the Konnect DEX Companion.
 * v2.5.883
 *
 * Tries the best available transport in this order:
 *   1. Web Serial API — the RIGHT approach for USB-to-DEX adapters (DEXtreme,
 *      VDI DEX Fetcher, Nayax BLE-Serial dongle). Works on Chrome/Edge desktop
 *      and Chrome Android over USB-OTG. Reads until we see a DEX end-of-file
 *      marker (`DXE` in EVA-DTS) or 5s idle timeout.
 *   2. Web Bluetooth — for aDEX BLE relays that expose a Nordic UART Service
 *      (streaming characteristic). Requires HTTPS.
 *   3. Simulated — canned DEX text for demo/testing on unsupported browsers.
 *
 * All three return `{ dex_text, transport, device_id, device_model }`.
 */

export const TRANSPORTS = {
  WEB_SERIAL: 'web_serial',
  WEB_BLUETOOTH: 'web_bluetooth',
  SIMULATED: 'simulated',
};

export function detectCapabilities() {
  return {
    web_serial: typeof navigator !== 'undefined' && !!navigator.serial,
    web_bluetooth: typeof navigator !== 'undefined' && !!navigator.bluetooth,
    is_secure: typeof window !== 'undefined' && (window.isSecureContext || window.location.protocol === 'https:'),
  };
}

// Nordic UART Service (used by most aDEX BLE modules and DIY Pi bridges)
const NUS_SERVICE = '6e400001-b5a3-f393-e0a9-e50e24dcca9e';
const NUS_TX_CHAR = '6e400003-b5a3-f393-e0a9-e50e24dcca9e';  // notify (data from device → us)

const SAMPLE_DEX = `DXS*STREAMWARE*V1L2*1*1
ST*001*0001
ID1*01*KRNT-DEMO-001*02*NAYAX-VPOS*03*USD*04*2*05*JETINNO-C4*06*2*07*8734*08*4200*09*100
CB1*01*04212*02*4200
MA5*01*15*02*0*03*0
VA1*01*1234500*02*4567
VA2*01*1150000*02*4210
CA1*01*350000*02*1400
DA1*01*885000*02*3170
PA1*01*A1*02*175
PA2*01*100*02*17500
PA1*01*A2*02*225
PA2*01*40*02*9000
PA1*01*B1*02*300
PA2*01*22*02*6600
EA1*01*A2*02*01*03*2*04*20260716
TA2*01*280000
G85*01*A1B7
SE*22*0001
DXE*1*1`;

async function readAllFromReader(reader, idleMs = 4000, hardTimeoutMs = 30000) {
  const decoder = new TextDecoder();
  let buffer = '';
  let lastRead = Date.now();
  const start = Date.now();
  const readOne = async () => {
    const { value, done } = await reader.read();
    if (done) return { done: true };
    if (value) buffer += decoder.decode(value, { stream: true });
    return { done: false };
  };
  while (true) {
    const race = Promise.race([
      readOne(),
      new Promise((resolve) => setTimeout(() => resolve({ timeout: true }), 1200)),
    ]);
    const step = await race;
    if (step.done) break;
    if (step.timeout) {
      if (Date.now() - lastRead > idleMs) break;
      continue;
    }
    lastRead = Date.now();
    if (/DXE\*/.test(buffer)) break;
    if (Date.now() - start > hardTimeoutMs) break;
  }
  return buffer;
}

/**
 * Attempt a Web Serial capture. Prompts the user for port permission.
 * Returns null if user cancels or Web Serial is unavailable.
 */
export async function captureViaWebSerial({ baudRate = 9600 } = {}) {
  if (!navigator.serial) throw new Error('Web Serial not supported on this browser');
  const port = await navigator.serial.requestPort();
  await port.open({ baudRate });
  const reader = port.readable.getReader();
  try {
    const dex_text = await readAllFromReader(reader);
    return {
      dex_text,
      transport: TRANSPORTS.WEB_SERIAL,
      device_id: port.getInfo?.().usbVendorId ? `usb:${port.getInfo().usbVendorId}:${port.getInfo().usbProductId}` : 'web_serial',
      device_model: 'usb_serial',
    };
  } finally {
    try { reader.releaseLock(); } catch (_) { /* noop */ }
    try { await port.close(); } catch (_) { /* noop */ }
  }
}

/**
 * Attempt a Web Bluetooth capture via Nordic UART Service.
 * Waits up to 30s for a `DXE*` end-marker.
 */
export async function captureViaWebBluetooth() {
  if (!navigator.bluetooth) throw new Error('Web Bluetooth not supported on this browser');
  const device = await navigator.bluetooth.requestDevice({
    filters: [{ services: [NUS_SERVICE] }],
    optionalServices: [NUS_SERVICE],
  });
  const server = await device.gatt.connect();
  const service = await server.getPrimaryService(NUS_SERVICE);
  const tx = await service.getCharacteristic(NUS_TX_CHAR);
  await tx.startNotifications();
  const decoder = new TextDecoder();
  let buffer = '';
  const start = Date.now();
  await new Promise((resolve, reject) => {
    const listener = (ev) => {
      const chunk = decoder.decode(ev.target.value);
      buffer += chunk;
      if (/DXE\*/.test(buffer) || Date.now() - start > 30000) {
        tx.removeEventListener('characteristicvaluechanged', listener);
        tx.stopNotifications().catch(() => {});
        resolve();
      }
    };
    tx.addEventListener('characteristicvaluechanged', listener);
    setTimeout(() => {
      tx.removeEventListener('characteristicvaluechanged', listener);
      resolve();
    }, 35000);
  });
  try { device.gatt.disconnect(); } catch (_) { /* noop */ }
  return {
    dex_text: buffer,
    transport: TRANSPORTS.WEB_BLUETOOTH,
    device_id: device.id,
    device_model: device.name || 'ble_dex_relay',
  };
}

/**
 * Simulated capture — resolves after a small delay with canned DEX text so
 * we can end-to-end test the flow on any browser.
 */
export async function captureSimulated({ delayMs = 900 } = {}) {
  await new Promise((r) => setTimeout(r, delayMs));
  return {
    dex_text: SAMPLE_DEX,
    transport: TRANSPORTS.SIMULATED,
    device_id: 'simulated-dex-001',
    device_model: 'konnect_simulator',
  };
}

export function autoTransport() {
  const c = detectCapabilities();
  if (c.web_serial) return TRANSPORTS.WEB_SERIAL;
  if (c.web_bluetooth) return TRANSPORTS.WEB_BLUETOOTH;
  return TRANSPORTS.SIMULATED;
}
